import type {
  ActivityLogEntry,
  EthocaAlert,
  EthocaStatus,
  EvidencePackage,
  EvidenceStatus,
  IntelEdge,
  MerchantSummary,
  MonthlyRow,
  SubscriberPoint,
  Tc40ReasonCode,
  Tc40Record,
  ThreeDsEvent,
} from "./types";
import { seeded } from "./simulation";

const REASON_CODES: Tc40ReasonCode[] = ["10.4", "13.1", "13.2", "13.5", "13.6", "13.7"];

function pick<T>(arr: T[], rng: () => number): T {
  return arr[Math.floor(rng() * arr.length)];
}

function monthDate(month: string, dayRng: () => number): string {
  const day = 1 + Math.floor(dayRng() * 27);
  return `${month}-${String(day).padStart(2, "0")}`;
}

function weeksBetween(startIso: string, count: number): string[] {
  const d = new Date(startIso);
  const out: string[] = [];
  for (let i = 0; i < count; i++) {
    out.push(d.toISOString().slice(0, 10));
    d.setDate(d.getDate() + 7);
  }
  return out;
}

// -------- TC40 records --------

export function buildTc40(
  merchants: MerchantSummary[],
  monthly: MonthlyRow[]
): Tc40Record[] {
  const out: Tc40Record[] = [];
  let idx = 0;
  merchants.forEach((m, mi) => {
    const rng = seeded(mi * 7919 + 13);
    const rows = monthly.filter((r) => r.merchant_id === m.id && r.active);
    rows.forEach((r) => {
      // Approx 1 record per 4 tc40 units, capped to avoid explosion
      const records = Math.min(8, Math.max(0, Math.round(r.tc40 / 4)));
      for (let i = 0; i < records; i++) {
        const amount = Math.round(m.price * (0.6 + rng() * 2.5));
        const matched = rng() < 0.55;
        out.push({
          id: `TC40-${String(++idx).padStart(5, "0")}`,
          merchantId: m.id,
          date: monthDate(r.month, rng),
          amountEur: amount,
          reasonCode: pick(REASON_CODES, rng),
          matchedCE3: matched,
          suppressed: matched && rng() < 0.85,
        });
      }
    });
  });
  return out;
}

// -------- Ethoca / CDRN alerts --------

export function buildEthoca(merchants: MerchantSummary[], now: string): EthocaAlert[] {
  const out: EthocaAlert[] = [];
  let idx = 0;
  const cutoff = new Date(now);
  const high = merchants.filter((m) => m.fraudRatioPct > 0.8 || m.cbMult >= 1.5).slice(0, 10);
  high.forEach((m, mi) => {
    const rng = seeded(mi * 3121 + 71);
    const count = 2 + Math.floor(rng() * 3);
    for (let i = 0; i < count; i++) {
      const offsetDays = Math.floor(rng() * 14);
      const received = new Date(cutoff);
      received.setDate(received.getDate() - offsetDays);
      const deadline = new Date(received);
      deadline.setDate(deadline.getDate() + 3);
      const status: EthocaStatus =
        offsetDays > 10 ? "expired" : offsetDays > 5 ? "acted" : "pending";
      const amount = Math.round(m.price * (0.8 + rng() * 3));
      out.push({
        id: `ETH-${String(++idx).padStart(4, "0")}`,
        merchantId: m.id,
        receivedAt: received.toISOString().slice(0, 10),
        deadlineAt: deadline.toISOString().slice(0, 10),
        amountEur: amount,
        cardLast4: String(1000 + Math.floor(rng() * 8999)),
        status,
        action:
          status === "pending"
            ? undefined
            : rng() < 0.55
              ? "refund"
              : rng() < 0.8
                ? "evidence"
                : "defer",
      });
    }
  });
  return out;
}

// -------- Evidence packages --------

export function buildEvidence(tc40: Tc40Record[]): EvidencePackage[] {
  return tc40
    .filter((r) => r.matchedCE3)
    .map((r, i) => {
      const rng = seeded(i * 101 + 17);
      const roll = rng();
      const status: EvidenceStatus = r.suppressed
        ? "submitted"
        : roll < 0.1
          ? "pending"
          : roll < 0.5
            ? "won"
            : roll < 0.75
              ? "lost"
              : "assembled";
      return {
        id: `EVD-${String(i + 1).padStart(5, "0")}`,
        merchantId: r.merchantId,
        disputeDate: r.date,
        reasonCode: r.reasonCode,
        amountEur: r.amountEur,
        status,
        assembledAt: r.date,
      };
    });
}

// -------- Subscriber weekly timelines --------

export function buildSubscribers(merchants: MerchantSummary[]): SubscriberPoint[] {
  const out: SubscriberPoint[] = [];
  merchants.forEach((m, mi) => {
    const rng = seeded(mi * 4201 + 9);
    const weeks = weeksBetween("2025-05-05", 52);
    let subs = Math.round(m.subs * 0.4);
    const target = m.terminateMo !== null ? Math.round(m.subs * 0.65) : m.subs;
    weeks.forEach((w, wi) => {
      const churnNoise = (rng() - 0.5) * m.subs * 0.01;
      subs = Math.round(subs + (target - subs) * 0.08 + churnNoise);
      if (m.terminateMo !== null && wi > 38) subs = Math.max(0, Math.round(subs * 0.7));
      out.push({ merchantId: m.id, week: w, subs: Math.max(0, subs) });
    });
  });
  return out;
}

// -------- 3DS step-up history --------

export function buildThreeDs(merchants: MerchantSummary[]): ThreeDsEvent[] {
  const out: ThreeDsEvent[] = [];
  let idx = 0;
  merchants
    .filter((m) => m.cbMult >= 1.2 || m.fraudRatioPct > 0.8)
    .forEach((m, mi) => {
      const rng = seeded(mi * 5051 + 31);
      const steps = 1 + Math.floor(rng() * 3);
      let gate = 0;
      for (let i = 0; i < steps; i++) {
        gate = Math.min(100, gate + 20 + Math.floor(rng() * 30));
        const whenDays = 210 - i * 45;
        const applied = new Date("2026-04-20");
        applied.setDate(applied.getDate() - whenDays);
        out.push({
          id: `3DS-${String(++idx).padStart(4, "0")}`,
          merchantId: m.id,
          appliedAt: applied.toISOString().slice(0, 10),
          gatePct: gate,
          fraudDeltaBps: -Math.round(40 + rng() * 60),
        });
      }
    });
  return out;
}

// -------- Cross-merchant intel graph --------

export function buildIntel(merchants: MerchantSummary[]): IntelEdge[] {
  const out: IntelEdge[] = [];
  const byMcc = new Map<string, MerchantSummary[]>();
  merchants.forEach((m) => {
    const arr = byMcc.get(m.mcc) ?? [];
    arr.push(m);
    byMcc.set(m.mcc, arr);
  });
  // shared-BIN (all merchants on same BIN-ish currency cohort)
  merchants.forEach((m, i) => {
    merchants.slice(i + 1).forEach((n) => {
      if (m.mcc === n.mcc && Math.abs(m.refundRatePct - n.refundRatePct) < 0.5) {
        out.push({
          source: m.id,
          target: n.id,
          kind: "refund-cluster",
          strength: Math.round((1 - Math.abs(m.refundRatePct - n.refundRatePct) / 10) * 100) /
            100,
          note: "tight refund-rate band; share billing pattern",
        });
      }
    });
  });
  // low-refund / high-fraud cluster (bad-actor correlation)
  const badActors = merchants.filter(
    (m) => m.refundRatePct < 0.5 && m.fraudRatioPct > 1.0
  );
  for (let i = 0; i < badActors.length - 1; i++) {
    out.push({
      source: badActors[i].id,
      target: badActors[i + 1].id,
      kind: "shared-ip-range",
      strength: 0.72,
      note: "low refund + rising vamp; suggests common provider / infra",
    });
  }
  return out.slice(0, 28);
}

// -------- Activity log --------

interface BuildActivityInput {
  now: string;
  merchants: MerchantSummary[];
  tc40: Tc40Record[];
  ethoca: EthocaAlert[];
}

export function buildActivity({
  now,
  merchants,
  tc40,
  ethoca,
}: BuildActivityInput): ActivityLogEntry[] {
  const entries: ActivityLogEntry[] = [];
  const cutoff = new Date(now);
  const pushFromOffset = (offsetDays: number, kind: ActivityLogEntry["kind"], summary: string, extra: Partial<ActivityLogEntry> = {}) => {
    const d = new Date(cutoff);
    d.setDate(d.getDate() - offsetDays);
    entries.push({
      id: `ACT-${String(entries.length + 1).padStart(5, "0")}`,
      at: d.toISOString(),
      kind,
      summary,
      ...extra,
    });
  };

  pushFromOffset(0, "scan", "overnight portfolio scan complete — 0.437% rolling, 1.063% clear of visa 1.5%.");

  // Recent interventions
  merchants
    .filter((m) => m.cbMult >= 1.2)
    .slice(0, 8)
    .forEach((m, i) => {
      pushFromOffset(i % 7, "intervention", `3ds step-up recommended for ${m.name}.`, {
        merchantId: m.id,
      });
    });

  // Recent evidence submissions
  tc40
    .filter((r) => r.suppressed)
    .slice(0, 6)
    .forEach((r, i) => {
      pushFromOffset(
        i,
        "evidence",
        `ce 3.0 evidence submitted for dispute ${r.id} — reason ${r.reasonCode}.`,
        { merchantId: r.merchantId, valueEur: r.amountEur }
      );
    });

  // Ethoca actions
  ethoca
    .filter((e) => e.status === "acted")
    .slice(0, 4)
    .forEach((e, i) => {
      pushFromOffset(
        i + 1,
        "alert",
        `ethoca alert ${e.id} resolved via ${e.action} on ${e.merchantId}.`,
        { merchantId: e.merchantId, valueEur: e.amountEur }
      );
    });

  return entries.sort((a, b) => b.at.localeCompare(a.at));
}
